import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, CheckCircle2, ShieldCheck } from "lucide-react";
import PageShell from "../components/sections/PageShell";
import { applySeo } from "../lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/hosting/setup`;
const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });

export default function HostingSetupPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(
    () =>
      applySeo({
        path: `/hosting/setup/${token}`,
        title: "Set up hosting billing — Gadget Guy Media",
        description: "Private hosting setup for AI Front Desk clients.",
        robots: "noindex, nofollow",
      }),
    [token]
  );

  useEffect(() => {
    const sid = params.get("session_id");
    axios
      .get(`${API}/${token}`, { params: sid ? { session_id: sid } : {} })
      .then(({ data }) => setInfo(data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not load this setup link."));
  }, [token, params]);

  const start = async () => {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await axios.post(`${API}/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  return (
    <PageShell testId="hosting-setup-page">
      <section className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="ggm-eyebrow mb-5">AI Front Desk · Hosting</div>
        {err && !info && (
          <p className="font-body text-red-200" data-testid="hosting-setup-error">{err}</p>
        )}
        {info && info.status === "active" && <Done info={info} />}
        {info && info.status !== "active" && (
          <div data-testid="hosting-setup-pending">
            <h1 className="font-condensed uppercase text-white leading-[1.02] text-4xl sm:text-5xl">
              Hi {info.name}, your first year is free.
            </h1>
            <p className="mt-6 font-body text-white/75 text-base md:text-lg leading-relaxed" data-testid="hosting-setup-terms">
              Basic app hosting and maintenance are free through <b className="text-white">{fmtDate(info.trial_end)}</b>,
              then <b className="text-white">$75 a month</b>. Save a card now and you won't be charged until then.
              You'll get a reminder email a month before, and you can cancel anytime.
            </p>
            <button type="button" onClick={start} disabled={busy} className="ggm-btn-primary mt-8 disabled:opacity-60" data-testid="hosting-setup-start-btn">
              {busy ? "Redirecting…" : "Save card & start free year"} <ArrowUpRight size={16} />
            </button>
            {err && <p className="mt-4 font-body text-red-200 text-sm" data-testid="hosting-setup-checkout-error">{err}</p>}
            <div className="mt-5 flex items-center gap-2 text-white/50 text-xs">
              <ShieldCheck size={14} className="text-[var(--ggm-signal-cyan)]" />
              Secure payment via Stripe · card details never touch our servers
            </div>
          </div>
        )}
      </section>
    </PageShell>
  );
}

const Done = ({ info }) => (
  <div data-testid="hosting-setup-active">
    <CheckCircle2 size={40} className="text-[var(--ggm-signal-cyan)]" />
    <h1 className="mt-4 font-condensed uppercase text-white leading-[1.02] text-4xl sm:text-5xl">You're all set.</h1>
    <p className="mt-6 font-body text-white/75 text-base md:text-lg leading-relaxed">
      Hosting billing is set up. Your free year runs through <b className="text-white">{fmtDate(info.trial_end)}</b>;
      your first $75 charge happens then. Manage your card or invoices anytime in the{" "}
      <Link to="/billing" className="underline text-white hover:text-[var(--ggm-signal-cyan)]">billing portal</Link>.
    </p>
  </div>
);
